// topological network graph (pipes + fittings) for path search, correlation and cable fault distance
import { haversine } from './model';

const ck = (p) => `c:${p[0].toFixed(6)},${p[1].toFixed(6)}`;
export function buildGraph(pipes, fittings, types) {
  const fx = Object.fromEntries((fittings || []).map((f) => [f.id, f]));
  const pos = {}, adj = {};
  const add = (a, b, len, pipe) => { (adj[a] ||= []).push({ to: b, len, pipe }); (adj[b] ||= []).push({ to: a, len, pipe }); };
  (pipes || []).filter((p) => !types || types.includes(p.type)).forEach((p) => {
    const ks = (p.points || []).map((pt) => {
      if (pt && pt.fid) { const f = fx[pt.fid]; if (!f) return null; pos['f:' + f.id] = [f.lat, f.lon]; return 'f:' + f.id; }
      if (!Array.isArray(pt)) return null;
      const k = ck(pt); pos[k] = pt; return k;
    }).filter(Boolean);
    for (let i = 1; i < ks.length; i++) if (ks[i] !== ks[i - 1]) add(ks[i - 1], ks[i], haversine(pos[ks[i - 1]], pos[ks[i]]), p);
  });
  return { pos, adj };
}

export function dijkstra(G, src) {
  const dist = { [src]: 0 }, prev = {}, done = new Set();
  for (;;) {
    let u = null;
    Object.keys(dist).forEach((k) => { if (!done.has(k) && (u == null || dist[k] < dist[u])) u = k; });
    if (u == null) break;
    done.add(u);
    (G.adj[u] || []).forEach((e) => { const d = dist[u] + e.len; if (dist[e.to] == null || d < dist[e.to] - 1e-9) { dist[e.to] = d; prev[e.to] = { from: u, e }; } });
  }
  return { dist, prev };
}

// node path a -> b with edges (null if not connected)
export function pathBetween(G, a, b) {
  if (!G.adj[a] || !G.adj[b]) return null;
  const { dist, prev } = dijkstra(G, a);
  if (dist[b] == null) return null;
  const edges = [];
  for (let k = b; k !== a; k = prev[k].from) edges.unshift({ from: prev[k].from, to: k, ...prev[k].e });
  return { len: dist[b], edges, nodes: [a, ...edges.map((e) => e.to)] };
}

const lerp = (p, q, u) => [p[0] + (q[0] - p[0]) * u, p[1] + (q[1] - p[1]) * u];
export function pointOnPath(G, path, x) {
  let s = 0;
  for (const e of path.edges) {
    if (x <= s + e.len || e === path.edges[path.edges.length - 1]) return lerp(G.pos[e.from], G.pos[e.to], e.len ? Math.min(1, Math.max(0, (x - s) / e.len)) : 0);
    s += e.len;
  }
  return G.pos[path.nodes[0]];
}

// consecutive edges of one pipe merged into sections (material / DN / length)
export const pathSections = (path) => path.edges.reduce((a, e) => {
  const l = a[a.length - 1];
  if (l && l.pipe.id === e.pipe.id) l.len += e.len; else a.push({ pipe: e.pipe, len: e.len });
  return a;
}, []);

// cable fault: ground distance g ± tol from start along all branches -> red zones + pins
export function faultZone(G, start, g, tol) {
  const { dist } = dijkstra(G, start), zones = [], pins = [], seen = new Set();
  Object.entries(G.adj).forEach(([u, es]) => es.forEach((e) => {
    const id = [u, e.to].sort().join('|') + e.pipe.id;
    if (seen.has(id) || dist[u] == null || dist[e.to] == null) return;
    seen.add(id);
    const [a, b] = dist[u] <= dist[e.to] ? [u, e.to] : [e.to, u];
    if (Math.abs(dist[b] - dist[a] - e.len) > 0.05 || !e.len) return;
    const lo = Math.max(0, g - tol - dist[a]), hi = Math.min(e.len, g + tol - dist[a]);
    if (hi > lo) zones.push([lerp(G.pos[a], G.pos[b], lo / e.len), lerp(G.pos[a], G.pos[b], hi / e.len)]);
    const c = g - dist[a];
    if (c >= 0 && c <= e.len) pins.push(lerp(G.pos[a], G.pos[b], c / e.len));
  }));
  return { zones, pins, reach: Math.max(0, ...Object.values(dist)) };
}

// typical sound velocity in water-filled pipes (m/s)
const VEL = [[/pe|hdpe|polyeth/i, 400], [/pvc/i, 480], [/az|asbest|ac\b|faserzement/i, 1050], [/ggg|duktil|ductile|sphäro/i, 1250], [/gg|guss|cast/i, 1200], [/stahl|steel|st\b/i, 1300], [/kupfer|copper|cu\b/i, 1250], [/beton|concrete/i, 1100]];
export const velOf = (mat) => VEL.find(([r]) => r.test(String(mat || '')))?.[1] || 1100;

// leak position x (m from sensor A) from time difference dt (ms, = arrival B - arrival A) over mixed sections
export function corrX(secs, dtMs) {
  const T = secs.reduce((s, x) => s + x.len / x.v, 0), tau = (T - dtMs / 1000) / 2;
  if (tau <= 0) return 0;
  let t = 0, x = 0;
  for (const s of secs) {
    const ts = s.len / s.v;
    if (t + ts >= tau) return x + (tau - t) * s.v;
    t += ts; x += s.len;
  }
  return x;
}
